// src/services/data/selectors.ts
import { Gathering, GatheringStatus, FabrikUser } from '../../types';
import { store } from './store';

// Get all gatherings for a specific status
export const getGatheringsByStatus = (status: GatheringStatus): Gathering[] => {
  const gatheringIds = store.views.byStatus[status] || [];
  return gatheringIds.map(id => store.entities.gatherings[id]).filter(Boolean);
};

// Get all gatherings for a specific date
export const getGatheringsByDate = (dateString: string): Gathering[] => {
  const gatheringIds = store.views.byDate[dateString] || [];
  return gatheringIds.map(id => store.entities.gatherings[id]).filter(Boolean);
};

// Get all gatherings across all statuses
export const getAllGatherings = (): Gathering[] => {
  return Object.values(store.entities.gatherings);
};

// Get a single gathering by ID
export const getGatheringById = (id: string): Gathering | undefined => {
  return store.entities.gatherings[id];
};

// Get loading state for a status
export const isStatusLoading = (status: GatheringStatus): boolean => {
  return store.metadata.isLoading.byStatus[status] || false;
};

// Get pagination info for a status
export const getPaginationForStatus = (status: GatheringStatus): {
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
} => {
  return store.metadata.pagination.byStatus[status] || { 
    currentPage: 0, 
    totalPages: 0, 
    hasMore: false 
  };
};

// Get last fetched timestamp for a status
export const getLastFetchedForStatus = (status: GatheringStatus): number => {
  return store.metadata.lastFetched.byStatus[status] || 0;
};

// Check if data for a status is stale (older than 30 seconds)
export const isStatusDataStale = (status: GatheringStatus): boolean => {
  const lastFetched = getLastFetchedForStatus(status);
  return Date.now() - lastFetched > 30000; // 30 seconds
};

// USER SELECTORS

// Get all users
export const getUsers = (): FabrikUser[] => {
  return Object.values(store.entities.users);
};

// Get a single user by ID
export const getUserById = (id: string): FabrikUser | undefined => {
  return store.entities.users[id];
};

// Get users loading state
export const isUsersLoading = (): boolean => {
  return store.metadata.isLoading.users || false;
};

// Get users pagination info
export const getUsersPagination = (): {
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
} => {
  return store.metadata.pagination.users || { 
    currentPage: 0, 
    totalPages: 0, 
    hasMore: false 
  };
};

// Get users last fetched timestamp
export const getUsersLastFetched = (): number => {
  return store.metadata.lastFetched.users || 0;
};

// Check if users data is stale (older than 30 seconds)
export const isUsersDataStale = (): boolean => {
  const lastFetched = getUsersLastFetched();
  return Date.now() - lastFetched > 30000; // 30 seconds
};

// Check if all user pages are being loaded
export const isLoadingAllUsers = (): boolean => {
  return store.metadata.isLoadingAllUsers || false;
};